import { useRef, useState } from 'react';
import Papa from 'papaparse';
import { toast } from 'sonner';
import { Import } from 'lucide-react';
import { Button } from '../button/button';
import { importPersonas } from '../../../lib/api';
import { normalizeKey } from '../../../lib/utils';
import { ImportPersonasProps } from './types';
import LoadingSpinner from '../loading-spinner';
import clsx from 'clsx';
import { SimpleTooltip } from '../tooltip';
import { knownPersonaColumns } from './data/known-persona-columns';
import posthog from '../../../lib/posthog';

const maxRows = 50;
const maxColumns = 50;
const maxFileSize = 1 * 1024 * 1024;
const requiredColumn = 'name';
const aiModelFallback = 'gpt-5.4';

const systemColumns = new Set([
  'id',
  'is_example',
  'user_id',
  'created_at',
  'updated_at',
  'model',
  'workspace_id',
  'model_id',
  'hidden_details',
  'status',
  'sector_id',
]);

const tooltipContent = `Import personas via a CSV file. The CSV must contain a "${requiredColumn}" column. Other columns will be mapped to known persona attributes or added as custom fields. Maximum ${maxRows} rows and ${maxColumns} columns allowed.`;

const ImportPersonas = ({
  authUser,
  aiModels,
  fetchPersonas,
}: ImportPersonasProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const defaultAiModel = aiModels?.find(
    (model) => model?.name === aiModelFallback,
  )?.id;

  const handleButtonClick = () => {
    if (!fileInputRef) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setIsLoading(true);
    const file = event?.target?.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv') || file.type !== 'text/csv') {
      toast.warning('Please upload a valid CSV file.');
      setIsLoading(false);
      return;
    }

    if (file.size > maxFileSize) {
      toast.warning('CSV file is too large (max 1MB).');
      setIsLoading(false);
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const parsedData = results.data as { [key: string]: any }[];

        if (!parsedData.length) {
          toast.warning('CSV is empty.');
          setIsLoading(false);
          return;
        }

        const firstRowKeys = Object.keys(parsedData[0]).map((k) =>
          normalizeKey(k),
        );
        if (!firstRowKeys.includes(requiredColumn)) {
          toast.warning(`CSV is missing required column: "${requiredColumn}"`);
          setIsLoading(false);
          return;
        }

        if (parsedData?.length > maxRows) {
          toast.warning(`Too many rows. The maximum allowed is ${maxRows}.`);
          setIsLoading(false);
          return;
        }

        const firstRow = parsedData?.[0] || {};
        const columnCount = Object.keys(firstRow)?.length;
        if (columnCount > maxColumns) {
          toast.warning(
            `Too many columns. The maximum allowed is ${maxColumns}.`,
          );
          setIsLoading(false);
          return;
        }

        const preparedPersonas = parsedData.map((row: Record<string, any>) => {
          const normalized: Record<string, any> = {};

          // Normalize all keys
          Object.entries(row).forEach(([key, value]) => {
            normalized[normalizeKey(key)] = value;
          });

          // Separate known columns
          const knownData: Record<string, any> = {};
          const unknownData: Record<string, any> = {};

          // Loop over original keys, but use normalized form only for comparison
          Object.entries(row).forEach(([originalKey, value]) => {
            const normalizedKey = normalizeKey(originalKey);

            if (systemColumns.has(normalizedKey)) {
              // Skip system/DB columns — they should never appear in notes
            } else if (normalizedKey === 'custom_field') {
              // Use the CSV value directly, without a "custom_field: " prefix
              knownData['custom_field'] = value;
            } else if (knownPersonaColumns.includes(normalizedKey)) {
              knownData[normalizedKey] = value;
            } else {
              // Keep the original key formatting for unknown fields
              unknownData[originalKey] = value;
            }
          });

          // Convert unknown fields into custom_field text
          const customFieldText = Object.entries(unknownData)
            .map(([k, v]) => `${k}: ${v}`)
            .join('\n');

          return {
            ...knownData,
            custom_field:
              [customFieldText, knownData.custom_field]
                .filter(Boolean)
                .join('\n') || null,
            workspace_id: authUser?.currentWorkspace?.workspace_id || null,
            user_id: authUser?.id || null,
            model_id: defaultAiModel || null,
          };
        });

        try {
          await importPersonas(
            preparedPersonas,
            authUser?.currentWorkspace?.workspace_id,
          );

          posthog.capture('personas_imported', {
            persona_count: preparedPersonas.length,
            workspace_id: authUser?.currentWorkspace?.workspace_id,
          });

          fetchPersonas();
          setIsLoading(false);
          toast.success('CSV file imported successfully.');
        } catch (error) {
          posthog.captureException(error);
          setIsLoading(false);
          toast.error('Failed to import personas. Please try again.');
        }
      },
      error: (error) => {
        setIsLoading(false);
        console.error('Failed to parse:', error);
        toast.error('There was an error parsing the CSV file.');
      },
    });

    event.target.value = '';
    setIsLoading(false);
  };

  return (
    <SimpleTooltip content={tooltipContent}>
      <div>
        <Button
          disabled={isLoading}
          className={clsx(
            'bg-prompt-safecomoutrageous-orange text-white hover:bg-prompt-safecomoutrageous-orange',
            isLoading && 'px-10',
          )}
          onClick={handleButtonClick}
        >
          {isLoading ? (
            <LoadingSpinner strokeWidth={7} />
          ) : (
            <>
              <Import /> Import batch
            </>
          )}
        </Button>
        <input
          type="file"
          accept=".csv"
          ref={fileInputRef}
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
    </SimpleTooltip>
  );
};

export default ImportPersonas;
